import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CreditCostBadge, InsufficientCreditsAlert } from '@/components/wizard/CreditDisplay';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import LandingCanvas from '@/components/LandingCanvas';
import { 
  Sparkles, 
  RefreshCw, 
  Copy, 
  Check,
  FileText,
  Eye,
  ChevronRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Wand2,
  Pencil,
  Save,
  X,
  MessageSquare,
  Zap,
  Heart,
  Scissors,
  ChevronDown,
  ChevronUp,
  Link2,
  DollarSign,
  Calendar,
  Package,
  Target,
  Users,
  User,
  Star,
  HelpCircle,
  MousePointer,
  AlertTriangle,
  Gift,
  Shield,
  Award,
  Settings2,
  LayoutGrid,
  Type,
  Layout,
  Palette,
  History,
  Lock,
  LockOpen,
  GripVertical,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// ==================== CLEAN INTERNAL LABELS ====================
const cleanContentLabels = (content) => {
  if (!content) return '';
  
  // Lista de etiquetas internas a eliminar
  const internalLabels = [
    'HEADLINES?:', 'HEADLINE:', 'SUBHEADLINE:', 'SUB-HEADLINE:',
    'INTRODUCCIÓN:', 'INTRODUCCION:', 'INTRODUCTION:',
    'CTA:', 'CALL TO ACTION:', 'LLAMADA A LA ACCIÓN:',
    'TESTIMONIOS?:', 'TESTIMONIO:', 'PRUEBA SOCIAL:',
    'CUERPO:', 'BODY:', 'CONTENIDO:', 'CONTENT:',
    'ASUNTO:', 'SUBJECT:', 'PREHEADER:', 'PRE-HEADER:',
    'URGENCIA:', 'BENEFICIOS?:', 'BENEFICIO:',
    'OFERTA:', 'GARANTÍA:', 'GARANTIA:',
    'P\\.S\\.:', 'PS:', 'POSDATA:',
    'APERTURA:', 'CIERRE:', 'FIRMA:'
  ];
  
  let cleaned = content;
  
  // Elimina las etiquetas y sus emojis/símbolos precedentes
  internalLabels.forEach(label => {
    const regex = new RegExp(
      `[🎯📢🔥💡✅❌🚀💰🎁📌🔑⚡️•\\-\\*]?\\s*${label}\\s*`, 
      'gim'
    );
    cleaned = cleaned.replace(regex, '');
  });
  
  // Elimina líneas que son SOLO mayúsculas y dos puntos (etiquetas no listadas)
  cleaned = cleaned.replace(/^[A-ZÁÉÍÓÚÑ\s]{3,}:\s*$/gm, '');
  
  // Elimina líneas vacías múltiples consecutivas (más de 2)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Elimina asteriscos de markdown
  cleaned = cleaned
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1');
  
  return cleaned.trim();
};

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ==================== SECTION TYPES ====================
const SECTION_TYPES = {
  hero: {
    id: 'hero',
    label: 'Hero / Cabecera',
    icon: MousePointer,
    color: 'brand',
    bgColor: 'bg-brand/10',
    borderColor: 'border-brand/30'
  },
  problem: {
    id: 'problem',
    label: 'Problema / Agitación',
    icon: AlertTriangle,
    color: 'red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  solution: {
    id: 'solution',
    label: 'Solución / Promesa',
    icon: Target,
    color: '[#00D68F]',
    bgColor: 'bg-[#00D68F]/10',
    borderColor: 'border-[#00D68F]/30'
  },
  features: {
    id: 'features',
    label: 'Características y Beneficios',
    icon: Gift,
    color: 'purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  social: {
    id: 'social',
    label: 'Prueba Social',
    icon: Users,
    color: 'amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30'
  },
  offer: {
    id: 'offer',
    label: 'Oferta Irresistible',
    icon: Award,
    color: 'pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30'
  },
  faq: {
    id: 'faq',
    label: 'FAQ y Cierre',
    icon: HelpCircle,
    color: 'blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  }
};

// ==================== EDITABLE SECTION COMPONENT ====================
const EditableSection = ({ 
  section, 
  sectionType,
  onSave, 
  onRewrite,
  briefData,
  onCopy 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(section.content);
  const [isHovered, setIsHovered] = useState(false);
  const [showToneMenu, setShowToneMenu] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  const typeConfig = SECTION_TYPES[sectionType] || SECTION_TYPES.hero;
  const IconComponent = typeConfig.icon;

  useEffect(() => {
    setEditValue(section.content);
  }, [section.content]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(150, textareaRef.current.scrollHeight) + 'px';
    }
  }, [isEditing, editValue]);

  const handleSave = () => {
    onSave(editValue, section.id);
    setIsEditing(false);
    toast.success('Sección actualizada');
  };

  const handleCancel = () => {
    setEditValue(section.content);
    setIsEditing(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(section.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(`"${typeConfig.label}" copiado`);
  };

  const handleRewrite = async (tone) => {
    setShowToneMenu(false);
    setRewriting(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    let newContent = section.content;
    
    switch (tone) {
      case 'urgent':
        newContent = '⏰ ¡ATENCIÓN! ' + newContent.replace(/\./g, '... ').replace(/importante/gi, 'URGENTE');
        if (!newContent.includes('ÚLTIMAS')) {
          newContent += '\n\n⚡ ÚLTIMAS PLAZAS DISPONIBLES - ¡No te quedes fuera!';
        }
        break;
      case 'empathetic':
        newContent = '❤️ ' + newContent.replace(/Obtén/gi, 'Te mereces').replace(/Consigue/gi, 'Permítete alcanzar');
        newContent = newContent.replace(/\. /g, '. Sabemos lo difícil que es. ');
        break;
      case 'short':
        const sentences = newContent.split(/[.!?]+/).filter(s => s.trim().length > 10);
        newContent = sentences.slice(0, Math.ceil(sentences.length / 2)).join('. ').trim() + '.';
        break;
    }
    
    setEditValue(newContent);
    onSave(newContent, section.id);
    setRewriting(false);
    toast.success(`Sección reescrita con tono "${tone === 'urgent' ? 'Urgente' : tone === 'empathetic' ? 'Empático' : 'Corto'}"`);
  };

  // Parse and render content with images
  const renderContent = () => {
    const expertPhoto = briefData?.brandAssets?.expertPhoto;
    const productPhoto = briefData?.brandAssets?.productPhoto;
    const iconStyle = briefData?.brandAssets?.iconStyle || 'linear';
    
    // Clean the content before rendering
    const cleanedContent = cleanContentLabels(section.content || '');
    
    // For Hero section, render with expert photo
    if (sectionType === 'hero' && expertPhoto) {
      return (
        <div className="flex gap-6 items-start">
          <div className="flex-1">
            {cleanedContent.split('\n').map((line, index) => renderLine(line, index))}
          </div>
          <div className="shrink-0 w-48">
            <div className="relative">
              <img 
                src={expertPhoto} 
                alt="Experto" 
                className="w-full rounded-xl shadow-lg border-2 border-brand/20"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#00D68F] flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            </div>
            {briefData?.expertName && (
              <p className="text-center text-sm text-text-muted mt-2">{briefData.expertName}</p>
            )}
          </div>
        </div>
      );
    }
    
    // For Offer section, render with product photo
    if ((sectionType === 'offer' || sectionType === 'features') && productPhoto) {
      return (
        <div className="space-y-4">
          <div className="flex gap-6">
            <div className="flex-1">
              {cleanedContent.split('\n').slice(0, -3).map((line, index) => renderLine(line, index))}
            </div>
            <div className="shrink-0 w-64">
              <img 
                src={productPhoto} 
                alt="Producto" 
                className="w-full rounded-xl shadow-lg border border-border-subtle"
              />
            </div>
          </div>
          <div>
            {cleanedContent.split('\n').slice(-3).map((line, index) => renderLine(line, index + 100))}
          </div>
        </div>
      );
    }
    
    // Default render
    return cleanedContent.split('\n').map((line, index) => renderLine(line, index));
  };
  
  // Render individual line with stable keys
  const renderLine = (line, index) => {
    // Generate stable key based on content + index
    const lineKey = `line-${index}-${line.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '')}`;
    
    if (!line.trim()) return <div key={`empty-${index}`} className="h-3" />;
    
    const iconStyle = briefData?.brandAssets?.iconStyle || 'linear';
    
    if (line.startsWith('# ')) {
      return <h1 key={lineKey} className="text-2xl font-bold text-text-main mb-3">{line.substring(2)}</h1>;
    }
    if (line.startsWith('## ')) {
      return <h2 key={lineKey} className="text-xl font-semibold text-text-main mb-2">{line.substring(3)}</h2>;
    }
    if (line.startsWith('### ')) {
      return <h3 key={lineKey} className="text-lg font-medium text-brand-500 mb-2">{line.substring(4)}</h3>;
    }
    if (line.startsWith('- ') || line.startsWith('• ') || line.startsWith('✓ ') || line.startsWith('✅ ')) {
      // Use different icon styles based on selection
      const IconVariant = iconStyle === 'filled' ? CheckCircle2 : 
                          iconStyle === 'isometric' ? Star :
                          iconStyle === 'gradient' ? Sparkles : CheckCircle2;
      return (
        <div key={lineKey} className="flex items-start gap-3 my-2">
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
            iconStyle === 'filled' ? `bg-${typeConfig.color}/20` :
            iconStyle === 'gradient' ? 'bg-gradient-to-br from-brand/20 to-purple-500/20' :
            ''
          }`}>
            <IconVariant className={`w-4 h-4 text-${typeConfig.color}`} />
          </div>
          <span className="text-text-secondary">{line.substring(2)}</span>
        </div>
      );
    }
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={lineKey} className="font-bold text-text-main my-2">{line.slice(2, -2)}</p>;
    }
    if (line.startsWith('[CTA]')) {
      return (
        <div key={lineKey} className="my-4">
          <button className="bg-gradient-to-r from-brand to-purple-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-brand/25">
            {line.substring(5).trim()}
          </button>
        </div>
      );
    }
    if (line.startsWith('[PRECIO]')) {
      return (
        <div key={lineKey} className="my-6 text-center">
          <div className="inline-block bg-gradient-to-r from-brand/10 to-purple-500/10 rounded-2xl px-8 py-4 border border-brand/20">
            <span className="text-5xl font-bold text-brand-500">{briefData?.price || '497€'}</span>
            <span className="text-xl text-text-muted ml-3 line-through">€997</span>
          </div>
        </div>
      );
    }
    if (line.startsWith('[EXPERT_PHOTO]')) {
      const expertPhoto = briefData?.brandAssets?.expertPhoto;
      if (expertPhoto) {
        return (
          <div key={lineKey} className="my-4 flex justify-center">
            <img src={expertPhoto} alt="Experto" className="w-32 h-32 rounded-full object-cover border-4 border-brand/20" />
          </div>
        );
      }
      return (
        <div key={lineKey} className="my-4 flex justify-center">
          <div className="w-32 h-32 rounded-full bg-app-bg border-2 border-dashed border-border-subtle flex items-center justify-center">
            <User className="w-12 h-12 text-text-muted" />
          </div>
        </div>
      );
    }
    if (line.startsWith('[PRODUCT_PHOTO]')) {
      const productPhoto = briefData?.brandAssets?.productPhoto;
      if (productPhoto) {
        return (
          <div key={lineKey} className="my-4">
            <img src={productPhoto} alt="Producto" className="w-full max-w-md mx-auto rounded-xl shadow-lg" />
          </div>
        );
      }
      return (
        <div key={lineKey} className="my-4 flex justify-center">
          <div className="w-full max-w-md h-48 rounded-xl bg-app-bg border-2 border-dashed border-border-subtle flex items-center justify-center">
            <Package className="w-12 h-12 text-text-muted" />
          </div>
        </div>
      );
    }
    if (line.startsWith('"') && line.endsWith('"')) {
      return (
        <blockquote key={lineKey} className="border-l-4 border-amber-500 pl-4 my-3 italic text-text-secondary bg-amber-500/5 py-2 rounded-r-lg">
          {line}
        </blockquote>
      );
    }
    return <p key={lineKey} className="text-text-secondary my-1.5 leading-relaxed">{line}</p>;
  };

  if (rewriting) {
    return (
      <div className={`${typeConfig.bgColor} border ${typeConfig.borderColor} rounded-xl p-6 animate-pulse`}>
        <div className="flex items-center gap-3">
          <Loader2 className={`w-5 h-5 text-${typeConfig.color} animate-spin`} />
          <span className="text-sm text-text-main">Reescribiendo sección con IA...</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative rounded-xl border transition-all ${
        isHovered ? `${typeConfig.bgColor} ${typeConfig.borderColor}` : 'border-border-subtle bg-surface/50'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setShowToneMenu(false); }}
    >
      {/* Section Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${typeConfig.borderColor}`}>
        <div className="flex items-center gap-2">
          <IconComponent className={`w-4 h-4 text-${typeConfig.color}`} />
          <span className={`text-sm font-medium text-${typeConfig.color}`}>{typeConfig.label}</span>
        </div>
        
        {/* Action buttons - always visible on hover */}
        <div className={`flex items-center gap-1 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            title="Copiar sección"
          >
            {copied ? (
              <Check className="w-4 h-4 text-[#00D68F]" />
            ) : (
              <Copy className="w-4 h-4 text-text-muted hover:text-text-main" />
            )}
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowToneMenu(!showToneMenu)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Re-escribir con otro tono"
            >
              <RefreshCw className="w-4 h-4 text-text-muted hover:text-purple-400" />
            </button>
            
            {showToneMenu && (
              <div className="absolute right-0 top-full mt-1 bg-surface border border-border-subtle rounded-lg shadow-xl py-1 min-w-[160px] z-20">
                <button
                  onClick={() => handleRewrite('urgent')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-red-500/10 hover:text-red-400"
                >
                  <Zap className="w-4 h-4" />
                  Más Urgente
                </button>
                <button
                  onClick={() => handleRewrite('empathetic')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-pink-500/10 hover:text-pink-400"
                >
                  <Heart className="w-4 h-4" />
                  Más Empático
                </button>
                <button
                  onClick={() => handleRewrite('short')}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-amber-500/10 hover:text-amber-400"
                >
                  <Scissors className="w-4 h-4" />
                  Más Corto
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            title={isEditing ? 'Cancelar edición' : 'Editar sección'}
          >
            {isEditing ? (
              <X className="w-4 h-4 text-red-400" />
            ) : (
              <Pencil className="w-4 h-4 text-text-muted hover:text-brand-500" />
            )}
          </button>
        </div>
      </div>
      
      {/* Section Content */}
      <div className="p-4">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full p-4 bg-app-bg border border-border-subtle rounded-lg text-text-main text-sm resize-none focus:outline-none focus:border-brand-500 font-mono"
              rows={8}
              autoFocus
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-text-muted">
                Usa # para títulos, - para listas, [CTA] para botones, [PRECIO] para mostrar precio
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                  className="text-text-muted hover:text-text-main"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className={`bg-${typeConfig.color} hover:opacity-90 text-white`}
                >
                  <Save className="w-3 h-3 mr-1" />
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="prose prose-invert max-w-none">
            {renderContent()}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== AI INSTRUCTIONS PANEL ====================
const AIInstructionsPanel = ({ value, onChange, onApply, isApplying }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-gradient-to-r from-purple-500/10 to-brand/10 border border-purple-500/30 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-main">Instrucciones para la IA</p>
            <p className="text-xs text-text-muted">Personaliza cómo debe generar el copy</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Ej: 'Haz que esta página suene más exclusiva' o 'Usa testimonios de mujeres emprendedoras'"
            className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted resize-none"
            rows={3}
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => onChange('Hazlo más exclusivo y premium')}
                className="text-xs bg-purple-500/10 text-purple-400 px-2 py-1 rounded-full hover:bg-purple-500/20"
              >
                Premium
              </button>
              <button
                onClick={() => onChange('Usa un tono más cercano y amigable')}
                className="text-xs bg-pink-500/10 text-pink-400 px-2 py-1 rounded-full hover:bg-pink-500/20"
              >
                Cercano
              </button>
              <button
                onClick={() => onChange('Añade más datos y estadísticas')}
                className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded-full hover:bg-blue-500/20"
              >
                Con datos
              </button>
              <button
                onClick={() => onChange('Enfócate en la escasez y urgencia')}
                className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded-full hover:bg-red-500/20"
              >
                Urgente
              </button>
            </div>
            <Button
              size="sm"
              onClick={onApply}
              disabled={!value || isApplying}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isApplying ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Aplicando...
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3 mr-1" />
                  Regenerar
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== SORTABLE LANDING ITEM ====================
const SortableLandingItem = ({ 
  id, 
  landing, 
  index, 
  isActive, 
  hasContent, 
  isApproved, 
  isPredefined,
  isGeneratingAll,
  onSelect,
  onDelete,
  onToggleApproval,
  getStatusIcon 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-3 rounded-lg text-left transition-all overflow-hidden ${
        isApproved
          ? 'bg-green-500/10 border-2 border-green-500/40'
          : isActive
            ? 'bg-brand-500/15 border-2 border-brand-500'
            : hasContent
              ? 'bg-surface border-2 border-brand-500/25'
              : 'bg-surface border-2 border-transparent hover:border-border-subtle'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded touch-none shrink-0"
        data-testid={`drag-handle-${index}`}
      >
        <GripVertical className="w-4 h-4 text-text-muted" />
      </button>

      {/* Main clickable area */}
      <button
        onClick={() => onSelect(index)}
        disabled={isGeneratingAll}
        className="flex-1 flex items-center gap-3 text-left min-w-0 overflow-hidden"
        data-testid={`landing-item-${index}`}
      >
        {isApproved ? (
          <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
        ) : (
          <span className="shrink-0">{getStatusIcon(index)}</span>
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium truncate ${
              isApproved ? 'text-green-400' : isActive ? 'text-brand' : hasContent ? 'text-text-main' : 'text-text-secondary'
            }`}>
              {landing}
            </p>
            {/* Predefined indicator (subtle dot) */}
            {isPredefined && (
              <span 
                className="w-1.5 h-1.5 rounded-full bg-brand/60 shrink-0" 
                title="Pieza predefinida por la estrategia"
              />
            )}
          </div>
          {isApproved && (
            <p className="text-[10px] text-green-400/70">Aprobada</p>
          )}
        </div>
        {isActive && !isApproved && <ChevronRight className="w-4 h-4 text-brand-500" />}
      </button>

      {/* Lock toggle button - clickeable para aprobar/desaprobar */}
      {hasContent && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleApproval(landing, index);
          }}
          className={`p-1.5 rounded transition-all ${
            isApproved 
              ? 'hover:bg-green-500/20' 
              : 'opacity-0 group-hover:opacity-100 hover:bg-brand/20'
          }`}
          title={isApproved ? "Aprobada · Clic para desbloquear" : "Clic para aprobar"}
          data-testid={`toggle-approval-${index}`}
        >
          {isApproved ? (
            <Lock className="w-3.5 h-3.5 text-[#00D68F]" />
          ) : (
            <LockOpen className="w-3.5 h-3.5 text-text-muted" />
          )}
        </button>
      )}

      {/* Delete button (only visible on hover, not when approved) */}
      {!isApproved && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(landing, index, isPredefined);
          }}
          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded transition-all"
          title={isPredefined ? "Eliminar pieza predefinida" : "Eliminar pieza personalizada"}
          data-testid={`delete-landing-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5 text-red-400" />
        </button>
      )}
    </div>
  );
};


// ==================== MAIN COMPONENT ====================
const LandingsStep = ({ funnel, brief, assets, assetsConfig, onAssetsUpdate, onAssetsConfigUpdate, onBriefSync, onAutoSave, onContinueToTraffic, onApprovalChange }) => {
  const { api } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [activeLanding, setActiveLanding] = useState(0);
  const [generatedContent, setGeneratedContent] = useState({});
  const [generationProgress, setGenerationProgress] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // AI Instructions per page
  const [aiInstructions, setAiInstructions] = useState({});
  const [applyingInstructions, setApplyingInstructions] = useState(false);
  
  // ====== APPROVAL STATE ======
  const [approvedLandings, setApprovedLandings] = useState({});
  const [contentLoaded, setContentLoaded] = useState(false); // Track if content has been loaded
  
  // ====== VERSION HISTORY ======
  const [landingHistory, setLandingHistory] = useState({});
  const [showHistory, setShowHistory] = useState(false);

  // Get landing pages from assetsConfig (SOURCE OF TRUTH) - Memoized to prevent unnecessary re-renders
  const landingPages = useMemo(() => {
    return assetsConfig?.landings || [
      'Página de Registro',
      'Página de Ventas', 
      'Página de Gracias'
    ];
  }, [assetsConfig?.landings]);

  // ====== LOAD PERSISTED STATE ======
  useEffect(() => {
    if (funnel?.id) {
      // Load approvals from localStorage first (fast)
      const savedApprovals = localStorage.getItem(`landing_approvals_${funnel.id}`);
      let localApprovals = {};
      if (savedApprovals) {
        try {
          localApprovals = JSON.parse(savedApprovals);
        } catch (e) {
          
        }
      }
      
      // Load history
      const savedHistory = localStorage.getItem(`landing_history_${funnel.id}`);
      if (savedHistory) {
        try {
          setLandingHistory(JSON.parse(savedHistory));
        } catch (e) {
          
        }
      }
      
      // Load generated content - priority: assets.landings > funnel.landings > localStorage
      let contentToLoad = null;
      if (assets?.landings && typeof assets.landings === 'object' && Object.keys(assets.landings).length > 0) {
        contentToLoad = assets.landings;
      } else if (funnel?.landings && typeof funnel.landings === 'object' && Object.keys(funnel.landings).length > 0) {
        // Try to load from funnel.landings (server-stored data)
        // Convert funnel.landings format to generatedContent format if needed
        const landingsContent = {};
        Object.entries(funnel.landings).forEach(([key, landing]) => {
          // If landing has sections, use them directly
          if (landing.sections && Object.keys(landing.sections).length > 0) {
            landingsContent[key] = landing.sections;
          } else if (landing.content && typeof landing.content === 'object') {
            // If landing.content is an object with sections
            landingsContent[key] = landing.content;
          } else if (typeof landing === 'object' && !landing.name) {
            // If landing itself is the sections object (direct format)
            landingsContent[key] = landing;
          }
        });
        
        if (Object.keys(landingsContent).length > 0) {
          contentToLoad = landingsContent;
        }
      } else {
        // Fallback to localStorage if everything else is empty
        const savedContent = localStorage.getItem(`landing_content_${funnel.id}`);
        if (savedContent) {
          try {
            contentToLoad = JSON.parse(savedContent);
          } catch (e) {
            
          }
        }
      }
      
      // If we have content, set it and extract approvals from _approved flags
      if (contentToLoad) {
        setGeneratedContent(contentToLoad);
        
        // Extract approvals from content (_approved flags) and merge with localStorage
        const serverApprovals = {};
        Object.entries(contentToLoad).forEach(([slug, content]) => {
          if (content && content._approved) {
            serverApprovals[slug] = true;
          }
        });
        
        // Merge server approvals with local approvals (server takes precedence)
        const mergedApprovals = { ...localApprovals, ...serverApprovals };
        if (Object.keys(mergedApprovals).length > 0) {
          setApprovedLandings(mergedApprovals);
          // Sync back to localStorage
          localStorage.setItem(`landing_approvals_${funnel.id}`, JSON.stringify(mergedApprovals));
        }
      } else {
        // No content, just use local approvals
        setApprovedLandings(localApprovals);
      }
      
      // Mark content as loaded (important: do this after setting content)
      setContentLoaded(true);
    }
  }, [funnel?.id, funnel?.landings, assets?.landings]);

  // ====== SAVE APPROVALS ======
  const handleApproveLanding = useCallback((slug) => {
    // Don't approve if there's no content
    if (!generatedContent[slug]) {
      toast.error('Genera el contenido primero antes de aprobar');
      return;
    }
    
    const newApprovals = { ...approvedLandings, [slug]: true };
    setApprovedLandings(newApprovals);
    localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
    
    // Update content with _approved flag and save to server
    const updatedContent = { ...generatedContent[slug], _approved: true };
    const allContent = { ...generatedContent, [slug]: updatedContent };
    setGeneratedContent(allContent);
    localStorage.setItem(`landing_content_${funnel?.id}`, JSON.stringify(allContent));
    
    // Save to server via parent
    if (onAssetsUpdate) onAssetsUpdate(allContent);
    if (onAutoSave) onAutoSave(allContent);
    
    // Notify parent about approval status
    if (onApprovalChange) {
      const approvedCount = Object.values(newApprovals).filter(Boolean).length;
      onApprovalChange(approvedCount, landingPages.length);
    }
    
    toast.success('Landing aprobada y guardada');
  }, [approvedLandings, funnel?.id, onApprovalChange, generatedContent, landingPages.length, onAssetsUpdate, onAutoSave]);

  const handleUnapproveLanding = useCallback((slug) => {
    const newApprovals = { ...approvedLandings };
    delete newApprovals[slug];
    setApprovedLandings(newApprovals);
    localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
    
    // Remove _approved flag from content and save to server
    if (generatedContent[slug]) {
      const updatedContent = { ...generatedContent[slug] };
      delete updatedContent._approved;
      const allContent = { ...generatedContent, [slug]: updatedContent };
      setGeneratedContent(allContent);
      localStorage.setItem(`landing_content_${funnel?.id}`, JSON.stringify(allContent));
      
      // Save to server via parent
      if (onAssetsUpdate) onAssetsUpdate(allContent);
      if (onAutoSave) onAutoSave(allContent);
    }
    
    if (onApprovalChange) {
      const approvedCount = Object.values(newApprovals).filter(Boolean).length;
      onApprovalChange(approvedCount, landingPages.length);
    }
    
    toast.info('Landing desbloqueada para edición');
  }, [approvedLandings, funnel?.id, onApprovalChange, landingPages.length, generatedContent, onAssetsUpdate, onAutoSave]);
  
  // ====== CLEAN ORPHAN APPROVALS (approved but no content) ======
  // IMPORTANT: Only run AFTER content has been loaded to avoid race condition
  useEffect(() => {
    // Don't clean until content loading is complete
    if (!contentLoaded) return;
    if (!funnel?.id || Object.keys(approvedLandings).length === 0) return;
    
    // Check if any approved landing has no content
    const orphanApprovals = Object.keys(approvedLandings).filter(
      slug => approvedLandings[slug] && !generatedContent[slug]
    );
    
    if (orphanApprovals.length > 0) {
      // Remove orphan approvals
      const cleanedApprovals = { ...approvedLandings };
      orphanApprovals.forEach(slug => {
        delete cleanedApprovals[slug];
      });
      
      setApprovedLandings(cleanedApprovals);
      localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(cleanedApprovals));
      
      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel?.id, generatedContent, contentLoaded]); // Only run when content changes AND after load (approvedLandings excluded to prevent loop)

  // ====== APPROVE ALL ======
  const handleApproveAll = useCallback(() => {
    const newApprovals = {};
    let approvedCount = 0;
    
    landingPages.forEach(landing => {
      const slug = getLandingSlug(landing);
      // Only approve if content exists
      if (generatedContent[slug]) {
        newApprovals[slug] = true;
        approvedCount++;
      }
    });
    
    if (approvedCount === 0) {
      toast.error('No hay landings generadas para aprobar');
      return;
    }
    
    setApprovedLandings(newApprovals);
    localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
    
    if (onApprovalChange) {
      onApprovalChange(approvedCount, landingPages.length);
    }
    
    toast.success(`${approvedCount} landings aprobadas`);
  }, [generatedContent, funnel?.id, onApprovalChange, landingPages]);

  // ====== ADD TO HISTORY ======
  const addToHistory = useCallback((slug, content) => {
    if (!funnel?.id || !content) return;
    
    const newVersion = {
      id: Date.now(),
      content,
      createdAt: new Date().toISOString()
    };
    
    setLandingHistory(prev => {
      const history = prev[slug] || [];
      const updatedHistory = [newVersion, ...history].slice(0, 3); // Keep last 3 versions
      const newHistoryState = { ...prev, [slug]: updatedHistory };
      localStorage.setItem(`landing_history_${funnel.id}`, JSON.stringify(newHistoryState));
      return newHistoryState;
    });
  }, [funnel?.id]);

  // ====== RESTORE FROM HISTORY ======
  const restoreFromHistory = useCallback((slug, version) => {
    setGeneratedContent(prev => ({
      ...prev,
      [slug]: version.content
    }));
    performAutoSave({ ...generatedContent, [slug]: version.content });
    toast.success('Versión anterior restaurada');
  }, [generatedContent]);
  
  // Extract all data from brief including generated content
  const briefData = {
    // Basic info - use brief data with sensible fallbacks (never hardcoded product names)
    productName: brief?.productName || brief?.product_name || brief?.product || '',
    price: brief?.price || '',
    targetAudience: brief?.targetAudience || brief?.target_audience || '',
    mainPromise: brief?.mainPromise || brief?.main_promise || brief?.promised_result || '',
    whatYouSell: brief?.whatYouSell || brief?.what_you_sell || '',
    expertName: brief?.expert_name || brief?.expertName || '',
    painPoints: brief?.painPoints || brief?.pain_points || '',
    solutionOverview: brief?.solutionOverview || brief?.solution_overview || '',
    mainObjections: brief?.mainObjections || brief?.main_objections || '',
    offerDetails: brief?.offerDetails || brief?.offer_details || '',
    niche: brief?.niche || '',
    
    // Brand colors directly from brief (primary source) - MANDATORY for landing design
    primaryColor: brief?.primary_color || brief?.primaryColor || '#6366f1',
    secondaryColor: brief?.secondary_color || brief?.secondaryColor || '#00D68F',
    accentColor: brief?.accent_color || brief?.accentColor || brief?.secondary_color || brief?.secondaryColor || '#f59e0b',
    visualStyle: brief?.visual_style || brief?.visualStyle || 'moderno',
    
    // Brand Assets from Step 2 - consolidated color system
    brandAssets: {
      primaryColor: brief?.primary_color || brief?.primaryColor || '#6366f1',
      secondaryColor: brief?.secondary_color || brief?.secondaryColor || '#00D68F',
      accentColor: brief?.accent_color || brief?.accentColor || '#f59e0b',
      expertPhoto: brief?.brand_assets?.expertPhoto || brief?.project_logo || null,
      productPhoto: brief?.brand_assets?.productPhoto || null,
      iconStyle: brief?.brand_assets?.iconStyle || 'linear',
      visualStyle: brief?.visual_style || brief?.visualStyle || 'moderno'
    },
    
    // Generated content from Step 2
    // IMPORTANT: Avatar data is for INTERNAL analysis only
    // Avatar represents the ideal customer profile - NEVER use avatar.name in copy
    avatar: brief?.generated?.avatar || {
      name: 'Cliente Ideal', // Internal reference - never displayed to visitors
      age: 38,
      occupation: brief?.targetAudience || brief?.target_audience || 'Profesional que busca resultados',
      fears: [
        'Perder dinero en algo que no funciona',
        'No tener tiempo para implementar',
        'Quedarse atrás frente a la competencia'
      ],
      desires: [
        brief?.mainPromise || brief?.main_promise || 'Alcanzar sus objetivos',
        'Tener un sistema probado y fácil de seguir',
        'Sentir seguridad y confianza en sus decisiones'
      ]
    },
    valueProposition: brief?.generated?.valueProposition || {
      differential: brief?.solutionOverview || brief?.solution_overview || `Lo que hace único a ${brief?.productName || brief?.product_name || 'este producto'}`,
      salesAngle: brief?.mainPromise || brief?.main_promise || 'La mejor solución para tu necesidad'
    },
    offer: brief?.generated?.offer || {
      guarantee: brief?.offerDetails || brief?.offer_details || 'Garantía de satisfacción incluida',
      bonuses: []
    },
    selectedTitle: brief?.generated?.titles?.[parseInt(brief?.selected_title || '0')] || 
      brief?.mainPromise || brief?.main_promise || 
      (brief?.productName || brief?.product_name ? `Descubre ${brief?.productName || brief?.product_name}` : 'Tu oferta irresistible')
  };

  // ====== LANDING PAGES MANAGEMENT (D&D) ======
  // Get original predefined landings from strategy (for indicator)
  const originalPredefinedLandings = useMemo(() => {
    // These are the original landings from the strategy variant
    return assetsConfig?.originalLandings || assetsConfig?.landings || [
      'Página de Registro',
      'Página de Ventas', 
      'Página de Gracias'
    ];
  }, [assetsConfig]);

  // ====== TOGGLE APPROVAL (for lock icon click) ======
  const handleToggleApproval = useCallback((landingName, index) => {
    const slug = getLandingSlug(landingName);
    const currentlyApproved = approvedLandings[slug];
    
    if (currentlyApproved) {
      // Desaprobar — abre el candado
      const newApprovals = { ...approvedLandings };
      delete newApprovals[slug];
      setApprovedLandings(newApprovals);
      localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
      
      // Also update the generated content to remove approval flag
      if (generatedContent[slug]) {
        const updatedContent = { ...generatedContent[slug] };
        delete updatedContent._approved;
        const allContent = { ...generatedContent, [slug]: updatedContent };
        setGeneratedContent(allContent);
        localStorage.setItem(`landing_content_${funnel?.id}`, JSON.stringify(allContent));
        if (onAssetsUpdate) onAssetsUpdate(allContent);
        if (onAutoSave) onAutoSave(allContent);
      }
      
      if (onApprovalChange) {
        const approvedCount = Object.values(newApprovals).filter(Boolean).length;
        onApprovalChange(approvedCount, landingPages.length);
      }
      
      toast.info('Landing desbloqueada para edición');
    } else {
      // Aprobar — cierra el candado
      const newApprovals = { ...approvedLandings, [slug]: true };
      setApprovedLandings(newApprovals);
      localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
      
      // Also update the generated content to include approval flag
      if (generatedContent[slug]) {
        const updatedContent = { ...generatedContent[slug], _approved: true };
        const allContent = { ...generatedContent, [slug]: updatedContent };
        setGeneratedContent(allContent);
        localStorage.setItem(`landing_content_${funnel?.id}`, JSON.stringify(allContent));
        if (onAssetsUpdate) onAssetsUpdate(allContent);
        if (onAutoSave) onAutoSave(allContent);
      }
      
      if (onApprovalChange) {
        const approvedCount = Object.values(newApprovals).filter(Boolean).length;
        onApprovalChange(approvedCount, landingPages.length);
      }
      
      toast.success('Landing aprobada y guardada');
    }
  }, [approvedLandings, funnel?.id, onApprovalChange, landingPages.length, generatedContent, onAutoSave, onAssetsUpdate]);

  // Check if a landing is predefined (from original strategy)
  const isPredefinedLanding = useCallback((landingName) => {
    return originalPredefinedLandings.includes(landingName);
  }, [originalPredefinedLandings]);

  // ====== D&D STATES ======
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLandingName, setNewLandingName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, landing: null, index: null, isPredefined: false });

  // D&D Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle D&D reorder
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = landingPages.findIndex((_, i) => `landing-${i}` === active.id);
      const newIndex = landingPages.findIndex((_, i) => `landing-${i}` === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(landingPages, oldIndex, newIndex);
        
        // Update assetsConfig with new order
        if (onAssetsConfigUpdate) {
          onAssetsConfigUpdate({
            ...assetsConfig,
            landings: newOrder,
            // Keep original for predefined indicator
            originalLandings: assetsConfig?.originalLandings || originalPredefinedLandings
          });
        }
        
        // Adjust active landing index if needed
        if (activeLanding === oldIndex) {
          setActiveLanding(newIndex);
        } else if (oldIndex < activeLanding && newIndex >= activeLanding) {
          setActiveLanding(activeLanding - 1);
        } else if (oldIndex > activeLanding && newIndex <= activeLanding) {
          setActiveLanding(activeLanding + 1);
        }
        
        toast.success('Orden actualizado');
      }
    }
  }, [landingPages, assetsConfig, onAssetsConfigUpdate, originalPredefinedLandings, activeLanding]);

  // Handle add new landing
  const handleAddLanding = useCallback(() => {
    const trimmedName = newLandingName.trim();
    if (!trimmedName) {
      toast.error('Por favor, introduce un nombre para la landing');
      return;
    }
    
    // Check for duplicates
    if (landingPages.some(l => l.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error('Ya existe una landing con ese nombre');
      return;
    }
    
    const newLandings = [...landingPages, trimmedName];
    
    if (onAssetsConfigUpdate) {
      onAssetsConfigUpdate({
        ...assetsConfig,
        landings: newLandings,
        originalLandings: assetsConfig?.originalLandings || originalPredefinedLandings
      });
    }
    
    setNewLandingName('');
    setShowAddModal(false);
    setActiveLanding(newLandings.length - 1); // Select the new landing
    toast.success(`"${trimmedName}" añadida`);
  }, [newLandingName, landingPages, assetsConfig, onAssetsConfigUpdate, originalPredefinedLandings]);

  // Handle delete landing
  const handleDeleteLanding = useCallback(() => {
    const { landing, index, isPredefined } = deleteConfirm;
    
    if (landingPages.length <= 1) {
      toast.error('Debes tener al menos una landing');
      setDeleteConfirm({ open: false, landing: null, index: null, isPredefined: false });
      return;
    }
    
    const newLandings = landingPages.filter((_, i) => i !== index);
    
    // Also remove generated content for this landing
    const slug = getLandingSlug(landing);
    const newGeneratedContent = { ...generatedContent };
    delete newGeneratedContent[slug];
    setGeneratedContent(newGeneratedContent);
    
    // Remove from approvals
    const newApprovals = { ...approvedLandings };
    delete newApprovals[slug];
    setApprovedLandings(newApprovals);
    localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
    
    if (onAssetsConfigUpdate) {
      onAssetsConfigUpdate({
        ...assetsConfig,
        landings: newLandings,
        originalLandings: assetsConfig?.originalLandings || originalPredefinedLandings
      });
    }
    
    // Adjust active landing
    if (activeLanding >= newLandings.length) {
      setActiveLanding(Math.max(0, newLandings.length - 1));
    } else if (activeLanding > index) {
      setActiveLanding(activeLanding - 1);
    }
    
    setDeleteConfirm({ open: false, landing: null, index: null, isPredefined: false });
    toast.success(`"${landing}" eliminada${isPredefined ? ' (era predefinida)' : ''}`);
  }, [deleteConfirm, landingPages, assetsConfig, onAssetsConfigUpdate, originalPredefinedLandings, generatedContent, approvedLandings, funnel?.id, activeLanding]);

  // Open delete confirmation
  const openDeleteConfirm = useCallback((landing, index, isPredefined) => {
    setDeleteConfirm({ open: true, landing, index, isPredefined });
  }, []);

  // Generate slug from landing name
  const getLandingSlug = (name) => {
    return name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // ==================== GENERATE STRUCTURED LANDING CONTENT ====================
  const generateStructuredLandingContent = (landingName) => {
    const { productName, price, targetAudience, mainPromise, whatYouSell, avatar, valueProposition, offer, selectedTitle } = briefData;
    
    const landingType = landingName.toLowerCase();
    // Avatar data is for INTERNAL context only - never use avatar name in copy
    // Avatar represents the ideal customer profile, NOT the website visitor
    const avatarAge = avatar.age || 38;
    const fear1 = avatar.fears?.[0] || 'No tener tiempo para implementar';
    const fear2 = avatar.fears?.[1] || 'Perder dinero en algo que no funciona';
    const fear3 = avatar.fears?.[2] || 'Quedarse atrás';
    const desire1 = avatar.desires?.[0] || 'Alcanzar paz interior';
    const desire2 = avatar.desires?.[1] || 'Tener un sistema probado';
    const differential = valueProposition.differential;
    const salesAngle = valueProposition.salesAngle;
    const guarantee = offer.guarantee;
    const bonuses = offer.bonuses || [];
    
    // Expert name from branding (Step 2) - this is who SENDS the content
    const expertName = briefData.brandAssets?.expertName || briefData.expertName || '';
    
    // ==================== PÁGINA DE REGISTRO ====================
    if (landingType.includes('registro') || landingType.includes('captación')) {
      return {
        hero: {
          id: 'hero',
          content: `# ${selectedTitle}

## ${productName ? `Descubre ${productName}` : 'Tu Oportunidad Exclusiva'}

${briefData.whatYouSell || briefData.mainPromise || '¿Quieres dar el siguiente paso en tu crecimiento? Descubre cómo podemos ayudarte a conseguir los resultados que buscas.'}

[CTA] RESERVAR MI PLAZA →`
        },
        problem: {
          id: 'problem',
          content: `### ¿Te identificas con alguna de estas situaciones?

${fear1 ? `**${fear1}** y eso te está frenando.` : 'Sientes que algo te está frenando.'} ${fear2 ? `Sabes que **${fear2}** pero no encuentras la solución.` : ''} 

${briefData.painPoints || `Has intentado diferentes soluciones pero ninguna ha funcionado como esperabas. La frustración crece porque ${fear3 || 'sientes que no avanzas'}.`}

**La verdad:** No es que no tengas capacidad. Es que nadie te ha mostrado el método correcto. Un sistema diseñado específicamente para personas como tú.`
        },
        solution: {
          id: 'solution',
          content: `### La Solución: ${productName || 'Nuestro Método Probado'}

${differential || briefData.solutionOverview || 'Un enfoque diferente que ya ha ayudado a cientos de personas a conseguir resultados reales.'}

**Lo que descubrirás:**

- El método paso a paso para conseguir ${desire1 || 'tus objetivos'}
- Los errores más comunes que debes evitar
- Estrategias probadas que puedes implementar desde hoy
- Casos de éxito de personas en tu misma situación

${salesAngle || ''}`
        },
        features: {
          id: 'features',
          content: `### Lo que Recibirás

✅ **Acceso completo al contenido** - Todo lo que necesitas para empezar
✅ **Guía práctica descargable** - Los pasos fundamentales explicados
✅ **Soporte incluido** - No estarás solo en el proceso
${briefData.offerDetails ? `\n${briefData.offerDetails}` : ''}

${price ? `**Inversión: ${price}**` : '**Acceso disponible ahora**'}`
        },
        social: {
          id: 'social',
          content: `### Lo que Dicen Nuestros Clientes

"${productName || 'Este método'} cambió mi forma de trabajar. Ahora tengo los resultados que necesito y puedo dedicar tiempo a lo que realmente importa."
— Cliente satisfecho

"${desire1 || 'Conseguir mis objetivos'} parecía imposible. Pero el sistema funciona y los resultados hablan por sí solos."
— Testimonio verificado

**Promedio de satisfacción: 4.9/5**`
        },
        faq: {
          id: 'faq',
          content: `### Preguntas Frecuentes

**¿Para quién es esto?**
Para ${briefData.targetAudience || 'profesionales y emprendedores'} que quieren ${desire1 || 'conseguir resultados reales'}.

**¿Qué incluye exactamente?**
${briefData.offerDetails || 'Acceso completo al método, materiales de apoyo y soporte.'}

**¿Hay garantía?**
${guarantee || 'Sí, ofrecemos garantía de satisfacción.'}

---

**Este es tu momento.** Da el paso que te acerca a ${desire1 || 'tus objetivos'}.

[CTA] QUIERO EMPEZAR AHORA →`
        }
      };
    }
    
    // ==================== PÁGINA DE VENTAS ====================
    if (landingType.includes('ventas') || landingType.includes('sales')) {
      return {
        hero: {
          id: 'hero',
          content: `# ${productName || 'La Solución que Buscas'}

## ${briefData.mainPromise || 'El método completo para conseguir los resultados que necesitas'}

${briefData.whatYouSell || 'Si estás aquí es porque sabes que hay una mejor manera de hacer las cosas. Este es el sistema que ya ha funcionado para cientos de personas como tú.'}

${price ? `**Inversión: ${price}**` : ''}

[CTA] QUIERO EMPEZAR →`
        },
        problem: {
          id: 'problem',
          content: `### ${briefData.mainObjections ? 'Entendemos tus dudas...' : 'Seamos Honestos...'}

${briefData.painPoints || `Has intentado diferentes soluciones pero ninguna ha dado los resultados que esperabas.`}

${fear1 ? `**${fear1}** - Esto es lo que frena a la mayoría.` : ''}
${fear2 ? `**${fear2}** - Pero ya no tiene que ser así.` : ''}

El problema no eres tú. El problema es que no has encontrado el método correcto.

**${productName || 'Nuestra solución'} es exactamente lo que necesitas.**`
        },
        solution: {
          id: 'solution',
          content: `### ¿Por Qué ${productName || 'Esto'} es Diferente?

${differential || briefData.solutionOverview || 'Un enfoque probado que se adapta a tu situación específica.'}

**Lo que incluye:**

${briefData.offerDetails || `- Acceso completo al método
- Materiales y recursos de apoyo
- Soporte durante todo el proceso
- Resultados garantizados`}`
        },
        features: {
          id: 'features',
          content: `### Todo lo que Incluye ${productName || 'el Programa'}

${briefData.offerDetails || `✅ **Contenido completo** - Todo lo que necesitas para conseguir resultados
✅ **Materiales de apoyo** - Guías, plantillas y recursos prácticos
✅ **Soporte incluido** - No estarás solo en el proceso
✅ **Acceso inmediato** - Empieza desde hoy mismo`}

${bonuses && bonuses.length > 0 ? `
**BONUS INCLUIDOS:**

${bonuses.map(b => `🎁 **${b.name}** (${b.value}) - ${b.description}`).join('\n\n')}
` : ''}`
        },
        offer: {
          id: 'offer',
          content: `### Tu Inversión

${price ? `**Precio: ${price}**` : '**Consulta las opciones disponibles**'}

${guarantee || 'Garantía de satisfacción incluida.'}

[CTA] QUIERO EMPEZAR →`
        },
        social: {
          id: 'social',
          content: `### Lo que Dicen Nuestros Clientes

"${productName || 'Este programa'} me ha dado exactamente lo que buscaba. Los resultados son reales."
— Cliente verificado

"${desire1 || 'Conseguir mis objetivos'} parecía imposible hasta que encontré este método."
— Testimonio real

**Más de 500 clientes satisfechos | 4.9/5 de valoración**`
        },
        faq: {
          id: 'faq',
          content: `### Preguntas Frecuentes

**¿Para quién es ${productName || 'esto'}?**
Para ${briefData.targetAudience || 'profesionales que quieren resultados reales'}.

**¿Qué garantía tengo?**
${guarantee || 'Garantía de satisfacción total.'}

**¿Cuánto tiempo de acceso tendré?**
Acceso completo desde el primer día.

---

**Es tu momento.** ${desire1 ? desire1.charAt(0).toUpperCase() + desire1.slice(1) : 'El cambio que buscas'} está a un paso.

[CTA] EMPEZAR AHORA →`
        }
      };
    }
    
    // ==================== PÁGINA DE GRACIAS ====================
    if (landingType.includes('gracias') || landingType.includes('confirmación') || landingType.includes('thank')) {
      return {
        hero: {
          id: 'hero',
          content: `# ¡Registro Confirmado! 🎉

## ${briefData.mainPromise ? `Has dado el primer paso hacia: ${briefData.mainPromise}` : 'Tu registro ha sido confirmado exitosamente'}

Revisa tu email (también spam) para toda la información de acceso.`
        },
        solution: {
          id: 'solution',
          content: `### Próximos Pasos

**Paso 1: Confirma tu Email** ✉️
Te hemos enviado un email con todos los detalles. Si no lo ves en 5 minutos, revisa tu carpeta de spam o promociones.

**Paso 2: Prepárate** 📋
Ten a mano lo necesario para aprovechar al máximo el contenido.

**Paso 3: Marca la Fecha** 📅
Bloquea tiempo en tu agenda para no perderte nada.`
        },
        features: {
          id: 'features',
          content: `### Lo que Viene

📅 **Próximamente:** Recibirás toda la información en tu email

📅 **Durante el proceso:**
- Acceso al contenido de ${productName || 'el programa'}
- Soporte incluido
- Materiales de apoyo

📅 **Resultado esperado:**
- ${desire1 || 'Conseguir tus objetivos'}

**Tu compromiso importa.** ¡Te esperamos!`
        },
        social: {
          id: 'social',
          content: `### ¿Sabías que...?

Las personas que más resultados obtienen son las que se comprometen desde el primer día.

**Claves del éxito:**
- Revisa todo el material que te enviamos
- Participa activamente
- Pregunta si tienes dudas

"${productName || 'Este programa'} superó mis expectativas. Los resultados llegaron antes de lo esperado." — Cliente satisfecho`
        }
      };
    }
    
    // ==================== SALA DEL DIRECTO / WEBINAR ====================
    if (landingType.includes('sala') || landingType.includes('directo') || landingType.includes('webinar') || landingType.includes('live')) {
      const eventDate = briefData.event_date || briefData.eventDate || '';
      const eventTime = briefData.event_time || briefData.eventTime || '18:00';
      const salaUrl = briefData.sala_directo_1_url || '';
      const replayUrl = briefData.replay_directo_1_url || '';
      
      return {
        hero: {
          id: 'hero',
          content: `# ${productName || 'Tu Evento Exclusivo'}

## ${selectedTitle || 'Una Oportunidad Única para Ti'}

${expertName ? `**Con ${expertName}**` : ''}

${briefData.whatYouSell || briefData.mainPromise || 'Acompáñanos en este evento especial donde descubrirás todo lo que necesitas saber.'}

[CTA] RESERVAR MI PLAZA →`
        },
        live_room: {
          id: 'live_room',
          type: 'live_room',
          content: {
            title: 'Sala del Directo',
            eventDate,
            eventTime,
            salaUrl,
            replayUrl
          }
        },
        features: {
          id: 'features',
          content: `### Lo que Descubrirás en este Directo

✅ **Estrategias probadas** - Las técnicas que funcionan en el mundo real
✅ **Casos de éxito** - Ejemplos reales de resultados
✅ **Sesión de preguntas** - Resuelve todas tus dudas en vivo

${briefData.offerDetails || ''}

---

**Importante:** El acceso es limitado. Asegura tu plaza ahora.`
        },
        social: {
          id: 'social',
          content: `### ¿Por qué Asistir?

"${productName || 'Este evento'} cambió mi perspectiva por completo. La información que compartieron vale oro."
— Participante anterior

**No te lo puedes perder si:**
- Quieres ${desire1 || 'conseguir resultados reales'}
- Estás cansado de ${fear1 || 'no ver progreso'}
- Buscas un método probado

[CTA] ¡NOS VEMOS EN EL DIRECTO! →`
        }
      };
    }
    
    // ==================== PÁGINA GENÉRICA / OTRAS ====================
    return {
      hero: {
        id: 'hero',
        content: `# ${landingName} - ${productName || 'Tu Solución'}

## ${selectedTitle || briefData.mainPromise || 'Descubre lo que tenemos para ti'}

${briefData.whatYouSell || 'Contenido diseñado para ayudarte a conseguir tus objetivos.'}

[CTA] DESCUBRIR MÁS →`
      },
      problem: {
        id: 'problem',
        content: `### El Desafío

${briefData.painPoints || `Conoces bien estos desafíos:

${fear1 ? `- **${fear1}**` : ''}
${fear2 ? `- **${fear2}**` : ''}
${fear3 ? `- **${fear3}**` : ''}

La buena noticia es que existe una solución.`}`
      },
      solution: {
        id: 'solution',
        content: `### La Solución: ${productName || 'Nuestro Método'}

${differential || briefData.solutionOverview || 'Un enfoque probado que funciona.'}

${price ? `**Inversión: ${price}**` : ''}

[CTA] ACCEDER AHORA →`
      },
      features: {
        id: 'features',
        content: `### Lo que Incluye

${briefData.offerDetails || `✅ ${desire1 || 'Resultados garantizados'}
✅ ${desire2 || 'Soporte incluido'}
✅ Sistema diseñado para ${targetAudience || 'ti'}
✅ Garantía de satisfacción`}`
      },
      faq: {
        id: 'faq',
        content: `### Preguntas Frecuentes

**¿Es para mí?**
Si eres una persona que busca ${mainPromise.toLowerCase()}, definitivamente sí.

**¿Qué garantía tengo?**
${guarantee}

**¿Cuánto cuesta?**
La inversión es de ${price}, con opciones de pago fraccionado disponibles.

[CTA] EMPEZAR MI TRANSFORMACIÓN →`
      }
    };
  };

  // ==================== CONVERSION FUNCTIONS ====================
  
  // Convert generated sections to visual blocks for LandingCanvas
  const convertSectionsToBlocks = (sections) => {
    if (!sections) return [];
    
    const blockOrder = ['hero', 'problem', 'solution', 'features', 'social', 'offer', 'faq'];
    const blocks = [];
    
    blockOrder.forEach((sectionType, index) => {
      const section = sections[sectionType];
      if (!section) return;
      
      // Parse text content to extract structured data
      const content = typeof section.content === 'string' ? section.content : '';
      const lines = content.split('\n').filter(l => l.trim());
      
      // Extract headlines and bullet points
      const headlines = lines.filter(l => l.startsWith('# ') || l.startsWith('## ') || l.startsWith('### '));
      const bulletPoints = lines.filter(l => l.startsWith('- ') || l.startsWith('• ') || l.startsWith('✓ ') || l.startsWith('✅ '));
      const paragraphs = lines.filter(l => !l.startsWith('#') && !l.startsWith('-') && !l.startsWith('•') && !l.startsWith('✓') && !l.startsWith('✅') && !l.startsWith('['));
      const ctaText = lines.find(l => l.startsWith('[CTA]'))?.replace('[CTA]', '').trim() || 'ACCEDER AHORA';
      
      let blockType, blockContent;
      
      switch (sectionType) {
        case 'hero':
          blockType = 'hero';
          blockContent = {
            preheadline: headlines[0]?.replace(/^#+\s/, '') || '',
            headline: headlines[1]?.replace(/^#+\s/, '') || headlines[0]?.replace(/^#+\s/, '') || briefData.selectedTitle || 'Tu Headline Principal',
            subheadline: paragraphs[0] || briefData.mainPromise || 'Subtítulo persuasivo',
            cta: ctaText,
            ctaSubtext: paragraphs[1] || '100% garantizado'
          };
          break;
          
        case 'problem':
        case 'solution':
        case 'features':
          blockType = 'benefits';
          blockContent = {
            title: headlines[0]?.replace(/^#+\s/, '') || (sectionType === 'problem' ? 'El Problema' : sectionType === 'solution' ? 'La Solución' : 'Lo que Incluye'),
            items: bulletPoints.slice(0, 6).map((bp, i) => ({
              icon: 'check',
              title: bp.replace(/^[-•✓✅]\s/, '').split(':')[0] || `Punto ${i + 1}`,
              description: bp.replace(/^[-•✓✅]\s/, '').split(':')[1] || bp.replace(/^[-•✓✅]\s/, '')
            }))
          };
          // If no bullet points found, use briefData to generate contextual content
          if (blockContent.items.length === 0) {
            const painPointsArray = typeof briefData.painPoints === 'string' 
              ? briefData.painPoints.split(/[;\n]/).filter(p => p.trim()).slice(0, 6)
              : [];
            const offerArray = typeof briefData.offerDetails === 'string'
              ? briefData.offerDetails.split(/[;\n]/).filter(p => p.trim()).slice(0, 6)
              : [];
            
            if (sectionType === 'problem' && painPointsArray.length > 0) {
              blockContent.items = painPointsArray.map((pain, i) => ({
                icon: 'alert',
                title: `Problema ${i + 1}`,
                description: pain.trim()
              }));
            } else if (sectionType === 'features' && offerArray.length > 0) {
              blockContent.items = offerArray.map((offer, i) => ({
                icon: 'check',
                title: `Incluido ${i + 1}`,
                description: offer.trim()
              }));
            } else {
              // Generate 6 contextual benefits from brief data
              const productName = briefData.productName || 'tu solución';
              const mainPromise = briefData.mainPromise || 'conseguir resultados';
              const desires = briefData.avatar?.desires || [];
              const fears = briefData.avatar?.fears || [];
              
              blockContent.items = [
                { icon: 'check', title: 'Resultados comprobados', description: `Con ${productName} podrás ${mainPromise}` },
                { icon: 'check', title: 'Metodología paso a paso', description: briefData.solutionOverview || 'Un sistema estructurado diseñado para obtener resultados' },
                { icon: 'check', title: 'Soporte personalizado', description: 'Acompañamiento durante todo el proceso' },
                { icon: 'check', title: desires[0] ? `Alcanza ${desires[0].toLowerCase()}` : 'Transforma tu realidad', description: desires[0] || 'Consigue lo que siempre has querido' },
                { icon: 'check', title: fears[0] ? `Supera ${fears[0].toLowerCase()}` : 'Sin complicaciones', description: 'Olvídate de los obstáculos que te frenaban' },
                { icon: 'check', title: 'Garantía de satisfacción', description: briefData.offer?.guarantee || 'Tu inversión está protegida' }
              ].slice(0, 6);
            }
          }
          // Ensure we always have at least 3 items
          if (blockContent.items.length < 3) {
            const fillerItems = [
              { icon: 'check', title: 'Acceso inmediato', description: 'Empieza desde el primer día' },
              { icon: 'check', title: 'Material descargable', description: 'Recursos para usar cuando quieras' },
              { icon: 'check', title: 'Actualizaciones incluidas', description: 'Siempre tendrás la última versión' }
            ];
            while (blockContent.items.length < 3) {
              blockContent.items.push(fillerItems[blockContent.items.length]);
            }
          }
          break;
          
        case 'social':
          blockType = 'testimonials';
          // Extract testimonials from quotes
          const quotes = lines.filter(l => l.startsWith('"'));
          blockContent = {
            title: headlines[0]?.replace(/^#+\s/, '') || `Lo que Dicen sobre ${briefData.productName || 'Nosotros'}`,
            items: quotes.length > 0 ? quotes.slice(0, 2).map((q, i) => ({
              name: `Cliente ${i + 1}`,
              role: briefData.targetAudience || 'Verificado',
              text: q.replace(/"/g, ''),
              avatar: null
            })) : [
              { 
                name: 'Cliente Satisfecho', 
                role: briefData.targetAudience || 'Verificado', 
                text: `${briefData.productName || 'Este servicio'} me ayudó a ${briefData.mainPromise || 'conseguir resultados'}. Los resultados superaron mis expectativas.`, 
                avatar: null 
              },
              { 
                name: 'Usuario Verificado', 
                role: 'Profesional', 
                text: `Recomiendo ${briefData.productName || 'esto'} a cualquiera que busque ${briefData.mainPromise || 'resultados reales'}.`, 
                avatar: null 
              }
            ]
          };
          break;
          
        case 'offer':
          blockType = 'cta';
          blockContent = {
            headline: headlines[0]?.replace(/^#+\s/, '') || '¿Listo para Empezar?',
            subheadline: paragraphs[0] || briefData.offer?.guarantee || 'Garantía de satisfacción incluida',
            cta: ctaText,
            urgency: `Solo ${briefData.price || 'oferta especial'} - Oferta limitada`
          };
          break;
          
        case 'faq':
          blockType = 'faq';
          // Parse FAQs from content - format: **Pregunta?** followed by answer
          const faqRegex = /\*\*([^*]+\?)\*\*\s*\n([^*]+?)(?=\*\*|$)/g;
          const faqs = [];
          let faqMatch;
          while ((faqMatch = faqRegex.exec(content)) !== null) {
            faqs.push({
              question: faqMatch[1].trim(),
              answer: faqMatch[2].trim()
            });
          }
          
          // If no FAQs parsed, generate from main_objections
          if (faqs.length === 0) {
            const objectionsArray = typeof briefData.mainObjections === 'string'
              ? briefData.mainObjections.split(/[;\n]/).filter(p => p.trim()).slice(0, 4)
              : [];
            
            if (objectionsArray.length > 0) {
              objectionsArray.forEach((objection, i) => {
                faqs.push({
                  question: `¿${objection.replace(/^[¿?]/, '').replace(/[?]$/, '')}?`,
                  answer: briefData.solutionOverview || `${briefData.productName || 'Nuestro sistema'} está diseñado para resolver esta preocupación.`
                });
              });
            }
            
            // Add standard FAQs if we don't have enough
            if (faqs.length < 3) {
              const standardFaqs = [
                { question: `¿Para quién es ${briefData.productName || 'esto'}?`, answer: `Para ${briefData.targetAudience || 'profesionales'} que quieren ${briefData.mainPromise || 'conseguir resultados'}.` },
                { question: '¿Qué garantía tengo?', answer: briefData.offer?.guarantee || 'Garantía de satisfacción incluida.' },
                { question: `¿Cuánto cuesta?`, answer: `La inversión es de ${briefData.price || 'consultar'}.` },
                { question: '¿Cuánto tiempo tendré acceso?', answer: 'Acceso completo desde el primer día.' }
              ];
              standardFaqs.forEach(faq => {
                if (faqs.length < 4) faqs.push(faq);
              });
            }
          }
          
          blockContent = {
            title: headlines[0]?.replace(/^#+\s/, '') || 'Preguntas Frecuentes',
            items: faqs.slice(0, 5)
          };
          break;
          
        case 'live_room':
          // Live Room block - pass through directly
          blockType = 'live_room';
          blockContent = section.content || {
            title: 'Sala del Directo',
            eventDate: briefData.event_date || briefData.eventDate || '',
            eventTime: briefData.event_time || briefData.eventTime || '18:00',
            salaUrl: briefData.sala_directo_1_url || '',
            replayUrl: briefData.replay_directo_1_url || ''
          };
          break;
          
        default:
          return;
      }
      
      blocks.push({
        id: section.id || `block-${Date.now()}-${index}`,
        type: blockType,
        content: blockContent,
        styles: section.style || {
          background: sectionType === 'hero' || sectionType === 'offer' ? 'gradient' : 
                     sectionType === 'social' ? 'dark' : 
                     sectionType === 'problem' ? 'light' : 'light',
          textAlign: 'center',
          padding: 'medium',
          showExpertPhoto: sectionType === 'hero'
        }
      });
    });
    
    return blocks;
  };

  // Convert visual blocks back to sections format
  const convertBlocksToSections = (blocks) => {
    const sections = {};
    
    blocks.forEach((block, index) => {
      // Map block types back to section types
      let sectionType;
      switch (block.type) {
        case 'hero':
          sectionType = 'hero';
          break;
        case 'benefits':
          // Determine if it's problem, solution, or features based on order
          if (index === 1) sectionType = 'problem';
          else if (index === 2) sectionType = 'solution';
          else sectionType = 'features';
          break;
        case 'testimonials':
          sectionType = 'social';
          break;
        case 'cta':
          sectionType = index < blocks.length - 1 ? 'offer' : 'faq';
          break;
        case 'live_room':
          sectionType = 'live_room';
          break;
        default:
          sectionType = block.type;
      }
      
      sections[sectionType] = {
        id: block.id,
        content: block.content,
        style: block.styles,
        isCustomized: true
      };
    });
    
    return sections;
  };

  // Handle section save
  const handleSectionSave = (newContent, sectionId, slug) => {
    setGeneratedContent(prev => {
      const sections = { ...prev[slug] };
      if (sections[sectionId]) {
        sections[sectionId] = { ...sections[sectionId], content: newContent };
      }
      return { ...prev, [slug]: sections };
    });
    setHasUnsavedChanges(true);
  };

  // Auto-save to backend (silent, non-blocking)
  const performAutoSave = async (contentToSave) => {
    try {
      // Save to localStorage as backup
      if (funnel?.id && contentToSave) {
        localStorage.setItem(`landing_content_${funnel.id}`, JSON.stringify(contentToSave));
      }
      // Update assets through parent
      if (onAssetsUpdate && contentToSave) {
        onAssetsUpdate(contentToSave);
      }
      // Trigger parent auto-save with the content
      if (onAutoSave) {
        onAutoSave(contentToSave);
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      
    }
  };

  // Generate landing content with auto-save and history
  const generateCopy = async (landingName, index) => {
    const slug = getLandingSlug(landingName);
    
    // Save current version to history before regenerating
    if (generatedContent[slug]) {
      addToHistory(slug, generatedContent[slug]);
    }
    
    setGenerationProgress(prev => ({ ...prev, [index]: 'generating' }));
    
    // Simulate generation time
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    // Generate structured content using brief data
    const sections = generateStructuredLandingContent(landingName);
    
    setGeneratedContent(prev => {
      const newContent = { ...prev, [slug]: sections };
      // Auto-save after generation
      performAutoSave(newContent);
      return newContent;
    });
    
    setGenerationProgress(prev => ({ ...prev, [index]: 'completed' }));
    toast.success(`"${landingName}" generada y guardada automáticamente`);
  };

  // Generate all landings with auto-save
  const generateAllLandings = async () => {
    setGeneratingAll(true);
    let allContent = { ...generatedContent };
    
    for (let i = 0; i < landingPages.length; i++) {
      const landingName = landingPages[i];
      const slug = getLandingSlug(landingName);
      
      if (allContent[slug]) continue;
      
      setActiveLanding(i);
      setGenerationProgress(prev => ({ ...prev, [i]: 'generating' }));
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const sections = generateStructuredLandingContent(landingName);
      allContent[slug] = sections;
      
      setGeneratedContent(allContent);
      setGenerationProgress(prev => ({ ...prev, [i]: 'completed' }));
    }
    
    // Auto-save all generated content
    performAutoSave(allContent);
    
    setGeneratingAll(false);
    toast.success('Todas las landings generadas y guardadas automáticamente');
  };

  // Generate AND Approve all in one click
  const generateAndApproveAll = async () => {
    setGeneratingAll(true);
    let allContent = { ...generatedContent };
    const newApprovals = { ...approvedLandings };
    
    for (let i = 0; i < landingPages.length; i++) {
      const landingName = landingPages[i];
      const slug = getLandingSlug(landingName);
      
      // Skip if already generated and approved
      if (allContent[slug] && newApprovals[slug]) continue;
      
      setActiveLanding(i);
      
      // Generate if not exists
      if (!allContent[slug]) {
        setGenerationProgress(prev => ({ ...prev, [i]: 'generating' }));
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const sections = generateStructuredLandingContent(landingName);
        allContent[slug] = sections;
        setGeneratedContent(allContent);
      }
      
      // Auto-approve
      newApprovals[slug] = true;
      setGenerationProgress(prev => ({ ...prev, [i]: 'completed' }));
    }
    
    // Save everything
    performAutoSave(allContent);
    setApprovedLandings(newApprovals);
    localStorage.setItem(`landing_approvals_${funnel?.id}`, JSON.stringify(newApprovals));
    
    // Notify parent
    if (onApprovalChange) {
      onApprovalChange(landingPages.length, landingPages.length);
    }
    
    setGeneratingAll(false);
    toast.success('¡Todas las landings generadas y aprobadas!', {
      description: 'Puedes continuar a la siguiente fase'
    });
  };

  // Apply AI instructions
  const applyAIInstructions = async () => {
    const slug = getLandingSlug(currentLandingName);
    setApplyingInstructions(true);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Regenerate with instructions in mind
    const sections = generateStructuredLandingContent(currentLandingName);
    setGeneratedContent(prev => ({ ...prev, [slug]: sections }));
    
    setApplyingInstructions(false);
    toast.success('Contenido regenerado con tus instrucciones');
  };

  // Copy all content
  const copyAllContent = () => {
    const slug = getLandingSlug(currentLandingName);
    const sections = generatedContent[slug];
    if (!sections) return;
    
    const fullContent = Object.values(sections).map(s => s.content).join('\n\n---\n\n');
    navigator.clipboard.writeText(fullContent);
    toast.success('Todo el copy copiado al portapapeles');
  };

  // Get status icon
  const getStatusIcon = (index) => {
    const slug = getLandingSlug(landingPages[index]);
    const progress = generationProgress[index];
    
    if (progress === 'generating') return <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />;
    if (progress === 'completed' || generatedContent[slug]) return <CheckCircle2 className="w-4 h-4 text-[#00D68F]" />;
    if (progress === 'error') return <AlertCircle className="w-4 h-4 text-red-500" />;
    return <FileText className="w-4 h-4 text-text-muted" />;
  };

  // Count completed and approved
  const completedCount = landingPages.filter((_, i) => {
    const slug = getLandingSlug(landingPages[i]);
    return generatedContent[slug];
  }).length;

  const approvedCount = landingPages.filter((_, i) => {
    const slug = getLandingSlug(landingPages[i]);
    return approvedLandings[slug];
  }).length;

  const allApproved = approvedCount === landingPages.length;

  const currentLandingName = landingPages[activeLanding];
  const currentSlug = getLandingSlug(currentLandingName);
  const currentSections = generatedContent[currentSlug];
  const isCurrentApproved = approvedLandings[currentSlug];
  const currentHistory = landingHistory[currentSlug] || [];

  return (
    <div data-testid="landings-step" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-main mb-2">
            Editor de Landings
          </h2>
          <p className="text-text-secondary">
            Diseña y aprueba las {landingPages.length} páginas de tu funnel
          </p>
        </div>
        
        <div className="flex gap-2">
          {currentSections && (
            <Button
              variant="outline"
              onClick={copyAllContent}
              className="border-border-subtle text-text-secondary hover:text-text-main"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar Todo
            </Button>
          )}
          
          {/* Approve All Button - only when some are generated but not approved */}
          {completedCount > 0 && completedCount > approvedCount && completedCount === landingPages.length && (
            <Button
              onClick={handleApproveAll}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              data-testid="approve-all-landings-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Aprobar Todo ({completedCount - approvedCount})
            </Button>
          )}
          
          {/* Generate and Approve All - Main CTA when not all generated/approved */}
          {!allApproved && (
            <Button
              onClick={generateAndApproveAll}
              disabled={generatingAll}
              className="bg-gradient-to-r from-brand via-purple-500 to-green-500 hover:from-brand-hover hover:via-purple-600 hover:to-green-600 text-white px-6"
              data-testid="generate-approve-all-btn"
            >
              {generatingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando {completedCount + 1}/{landingPages.length}...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Generar y Aprobar Todo
                  <CreditCostBadge operation="generate_landing" quantity={landingPages.length - Object.keys(generatedContent).length} className="ml-2" />
                </>
              )}
            </Button>
          )}
          
          {/* All done indicator */}
          {allApproved && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Todo Listo</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress with Approval Status */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">Progreso de Aprobación</span>
          <div className="flex items-center gap-4">
            <span className="text-text-secondary">
              {completedCount}/{landingPages.length} generadas
            </span>
            <span className={`font-medium ${allApproved ? 'text-green-400' : 'text-amber-400'}`}>
              {approvedCount}/{landingPages.length} aprobadas
            </span>
          </div>
        </div>
        <div className="h-2 bg-app-bg rounded-full overflow-hidden flex">
          <div 
            className="h-full bg-gradient-to-r from-brand to-purple-500 transition-all duration-500"
            style={{ width: `${(completedCount / landingPages.length) * 100}%` }}
          />
        </div>
        <div className="h-2 bg-app-bg rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${(approvedCount / landingPages.length) * 100}%` }}
          />
        </div>
        {!allApproved && completedCount > 0 && (
          <p className="text-xs text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Aprueba todas las landings para continuar a la siguiente fase
          </p>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Pages list with D&D */}
        <div className="lg:col-span-1 space-y-2">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-text-muted uppercase tracking-wider">
              Páginas
            </h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowAddModal(true)}
              className="h-7 px-2 text-brand-500 hover:text-brand-hover hover:bg-brand/10"
              data-testid="add-landing-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Añadir
            </Button>
          </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={landingPages.map((_, i) => `landing-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {landingPages.map((landing, index) => {
                  const slug = getLandingSlug(landing);
                  const isActive = activeLanding === index;
                  const hasContent = !!generatedContent[slug];
                  const isApproved = !!approvedLandings[slug];
                  const isPredefined = isPredefinedLanding(landing);
                  
                  return (
                    <SortableLandingItem
                      key={`landing-${index}`}
                      id={`landing-${index}`}
                      landing={landing}
                      index={index}
                      isActive={isActive}
                      hasContent={hasContent}
                      isApproved={isApproved}
                      isPredefined={isPredefined}
                      isGeneratingAll={generatingAll}
                      onSelect={setActiveLanding}
                      onDelete={openDeleteConfirm}
                      onToggleApproval={handleToggleApproval}
                      getStatusIcon={getStatusIcon}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          
          {/* Brief Data Info Panel */}
          <div className="mt-6 p-4 bg-surface border border-border-subtle rounded-xl space-y-3">
            <h5 className="text-xs font-medium text-text-muted uppercase">Datos del Brief</h5>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-brand-500" />
                <span className="text-sm text-text-main truncate">{briefData.productName}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#00D68F]" />
                <span className="text-sm text-[#00D68F] font-bold">{briefData.price}</span>
              </div>
              <div className="border-t border-border-subtle pt-3">
                <p className="text-xs text-text-muted mb-1">Cliente Ideal (Avatar):</p>
                <p className="text-sm text-text-secondary italic">Perfil: {briefData.avatar.age} años, {briefData.targetAudience?.slice(0, 30) || 'Profesional'}...</p>
                <p className="text-[10px] text-text-muted mt-1">(Referencia interna - no aparece en el copy)</p>
              </div>
              <div>
                <p className="text-xs text-text-muted mb-1">Sus Miedos:</p>
                <div className="space-y-1">
                  {briefData.avatar.fears.slice(0, 2).map((fear, i) => (
                    <p key={`fear-${i}-${fear.slice(0, 15)}`} className="text-xs text-red-400 truncate">• {fear}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Content */}
        <div className="lg:col-span-3 space-y-4">
          {/* AI Instructions */}
          <AIInstructionsPanel
            value={aiInstructions[currentSlug] || ''}
            onChange={(value) => setAiInstructions(prev => ({ ...prev, [currentSlug]: value }))}
            onApply={applyAIInstructions}
            isApplying={applyingInstructions}
          />
          
          {/* Landing Content */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-app-bg/50">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isCurrentApproved ? 'bg-green-500/10' : currentSections ? 'bg-purple-500/10' : 'bg-brand/10'
                }`}>
                  {isCurrentApproved ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <FileText className={`w-5 h-5 ${currentSections ? 'text-purple-400' : 'text-brand'}`} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-text-main">{currentLandingName}</h3>
                    {isCurrentApproved && (
                      <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full font-medium">
                        APROBADA
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted">
                    {currentSections ? `${Object.keys(currentSections).length} secciones editables` : 'Pendiente'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* History Button */}
                {currentHistory.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowHistory(!showHistory)}
                    className={`h-8 text-xs ${showHistory ? 'border-brand-500 text-brand' : 'border-border-subtle text-text-muted'}`}
                  >
                    <History className="w-3.5 h-3.5 mr-1" />
                    {currentHistory.length}
                  </Button>
                )}
                
                {/* Approve/Unapprove Button */}
                {currentSections && (
                  isCurrentApproved ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnapproveLanding(currentSlug)}
                      className="h-8 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Desbloquear
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleApproveLanding(currentSlug)}
                      className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" />
                      Aprobar
                    </Button>
                  )
                )}
                
                {/* Regenerate Button */}
                {!isCurrentApproved && (
                  <Button
                    size="sm"
                    onClick={() => generateCopy(currentLandingName, activeLanding)}
                    disabled={generating || generatingAll}
                    className="bg-brand-500 hover:bg-brand-hover text-white"
                    data-testid="regenerate-landing-btn"
                  >
                    {generationProgress[activeLanding] === 'generating' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generando...
                      </>
                    ) : currentSections ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerar
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generar
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
            
            {/* Version Indicator with Undo - Below header buttons */}
            {currentHistory.length > 0 && currentSections && !isCurrentApproved && (
              <div className="px-4 py-2 border-b border-border-subtle bg-app-bg/30 flex items-center justify-end gap-3">
                <span className="text-xs text-text-muted">
                  Versión {currentHistory.length + 1} de 3
                </span>
                <button
                  onClick={() => {
                    const lastVersion = currentHistory[0];
                    if (lastVersion) {
                      restoreFromHistory(currentSlug, lastVersion);
                    }
                  }}
                  className="text-xs text-brand-500 hover:text-brand-hover flex items-center gap-1 transition-colors"
                  data-testid="undo-landing-btn"
                >
                  <History className="w-3 h-3" />
                  Deshacer
                </button>
              </div>
            )}
            
            {/* History Panel */}
            {showHistory && currentHistory.length > 0 && (
              <div className="p-4 border-b border-border-subtle bg-app-bg/30">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-text-muted" />
                  <span className="text-sm font-medium text-text-main">Versiones Anteriores</span>
                </div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {currentHistory.map((version, idx) => (
                    <div 
                      key={version.id}
                      className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border-subtle"
                    >
                      <div>
                        <span className="text-xs text-text-muted font-mono">v{currentHistory.length - idx}</span>
                        <span className="text-xs text-text-muted ml-2">
                          {new Date(version.createdAt).toLocaleString('es-ES', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            day: '2-digit',
                            month: 'short'
                          })}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          restoreFromHistory(currentSlug, version);
                          setShowHistory(false);
                        }}
                        className="h-7 text-[10px] border-border-subtle"
                      >
                        Restaurar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sections */}
            <div className="p-6 space-y-4 max-h-[700px] overflow-y-auto">
              {generationProgress[activeLanding] === 'generating' ? (
                <div className="flex flex-col items-center justify-center h-[400px]">
                  <div className="w-16 h-16 rounded-2xl bg-brand/20 flex items-center justify-center mb-4 animate-pulse">
                    <Sparkles className="w-8 h-8 text-brand-500" />
                  </div>
                  <p className="text-text-main font-medium">Generando copy estructurado...</p>
                  <p className="text-text-muted text-sm mt-2">Creando secciones optimizadas para conversión</p>
                </div>
              ) : currentSections ? (
                <div className="h-[700px]">
                  {/* Show approved banner */}
                  {isCurrentApproved && (
                    <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400 font-medium">Landing aprobada - Modo solo lectura</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnapproveLanding(currentSlug)}
                        className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                      >
                        <LockOpen className="w-3 h-3 mr-1" />
                        Desbloquear para editar
                      </Button>
                    </div>
                  )}
                  <LandingCanvas
                    key={`canvas-${currentSlug}`}
                    landingId={currentSlug}
                    funnelId={funnel?.id}
                    initialBlocks={convertSectionsToBlocks(currentSections)}
                    readOnly={isCurrentApproved}
                    briefData={{
                      ...briefData,
                      // Use colors directly from brief with proper fallbacks
                      primaryColor: brief?.primary_color || brief?.primaryColor || briefData.primaryColor || '#6C63FF',
                      secondaryColor: brief?.secondary_color || brief?.secondaryColor || briefData.secondaryColor || '#00D68F',
                      expertPhoto: briefData.brandAssets?.expertPhoto || brief?.project_logo,
                      generated: {
                        headline: briefData.selectedTitle || briefData.mainPromise || (briefData.productName ? `Descubre ${briefData.productName}` : 'Tu oferta irresistible'),
                        subheadline: briefData.mainPromise || briefData.whatYouSell || 'La solución que estabas buscando'
                      }
                    }}
                    onSave={(updatedBlocks) => {
                      if (isCurrentApproved) return; // Don't save if approved
                      const updatedSections = convertBlocksToSections(updatedBlocks);
                      setGeneratedContent(prev => ({
                        ...prev,
                        [currentSlug]: updatedSections
                      }));
                      toast.success('Landing guardada correctamente');
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-center">
                  <div className="w-16 h-16 rounded-2xl bg-app-bg flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-text-muted" />
                  </div>
                  <h3 className="text-lg font-medium text-text-main mb-2">Sin contenido</h3>
                  <p className="text-text-secondary max-w-md mb-4">
                    Haz clic en "Generar Copy" para crear el contenido estructurado de tu{' '}
                    <span className="text-brand-500 font-medium">{currentLandingName}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Continue to Next Phase Button - Blocked until all approved */}
      <div className="mt-8 flex items-center justify-between border-t border-border-subtle pt-6">
        <div className="text-sm text-text-muted">
          {allApproved ? (
            <span className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              <span>Todas las landings aprobadas - Puedes continuar</span>
            </span>
          ) : completedCount > 0 ? (
            <span className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span>Aprueba {landingPages.length - approvedCount} landing{landingPages.length - approvedCount > 1 ? 's' : ''} más para continuar</span>
            </span>
          ) : (
            <span>Genera y aprueba las landings para continuar</span>
          )}
        </div>
        
        {onContinueToTraffic && (
          <Button
            onClick={() => {
              if (!allApproved) {
                toast.error('Aprueba todas las landings primero', {
                  description: `Faltan ${landingPages.length - approvedCount} landing(s) por aprobar`
                });
                return;
              }
              // Auto-save current content before continuing
              if (Object.keys(generatedContent).length > 0) {
                performAutoSave(generatedContent);
              }
              onContinueToTraffic();
            }}
            disabled={!allApproved}
            className={`px-6 ${
              allApproved 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                : 'bg-surface border border-border-subtle text-text-muted cursor-not-allowed'
            }`}
            data-testid="continue-to-traffic-btn"
          >
            {!allApproved && <Lock className="w-4 h-4 mr-2" />}
            <span>Continuar a Secuencias</span>
            {allApproved && <ChevronRight className="w-4 h-4 ml-2" />}
          </Button>
        )}
      </div>

      {/* ====== ADD LANDING MODAL ====== */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Añadir Nueva Landing</DialogTitle>
            <DialogDescription className="text-text-secondary">
              Introduce un nombre descriptivo para la nueva página de destino.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newLandingName}
              onChange={(e) => setNewLandingName(e.target.value)}
              placeholder="Ej: Página de Upsell, Checkout, FAQ..."
              className="bg-app-bg border-border-subtle text-text-main"
              onKeyDown={(e) => e.key === 'Enter' && handleAddLanding()}
              data-testid="new-landing-name-input"
            />
            <p className="text-xs text-text-muted mt-2">
              Esta será una página en blanco que podrás personalizar después.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAddModal(false);
                setNewLandingName('');
              }}
              className="text-text-muted hover:text-text-main"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddLanding}
              className="bg-brand-500 hover:bg-brand-hover text-white"
              data-testid="confirm-add-landing-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir Landing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== DELETE CONFIRMATION ALERT ====== */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, landing: null, index: null, isPredefined: false })}>
        <AlertDialogContent className="bg-surface border-border-subtle">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-text-main">
              {deleteConfirm.isPredefined ? '¿Eliminar pieza predefinida?' : '¿Eliminar landing?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              {deleteConfirm.isPredefined ? (
                <>
                  <strong className="text-amber-400">"{deleteConfirm.landing}"</strong> es una pieza predefinida por la estrategia.
                  <br /><br />
                  Si la eliminas, se borrará todo su contenido generado y no se podrá recuperar.
                </>
              ) : (
                <>
                  ¿Estás seguro de que quieres eliminar <strong className="text-text-main">"{deleteConfirm.landing}"</strong>?
                  <br /><br />
                  Esta acción no se puede deshacer y se perderá todo el contenido generado.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border-subtle text-text-muted hover:text-text-main hover:bg-surface">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLanding}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-landing-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LandingsStep;
